import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from '../misc/Button';
import ToggleSwitch from '../misc/ToggleSwitch';
import ParticipantPicker from '../misc/ParticipantPicker';
import Modal from '../layouts/Modal';
import { createScoreCard, ScoreCard, ScoreCardType } from '../../services/scoreCardService';
import { getScoreCardTypeLabelKey } from '../../utils/scoreCardUtil';

type ScoreCardFormData = {
  name: string;
  description?: string;
  memberEditAccess: boolean;
  containsAllMembers?: boolean;
  selectedUserIds?: number[];
  ascending?: boolean;
  type: ScoreCardType;
};

interface ScoreCardModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCreated?: (card: ScoreCard) => void;
  initialState?: ScoreCardFormData;
  communityId?: number;
}

export default function ScoreCardModal({ open, setOpen, onCreated, initialState, communityId }: ScoreCardModalProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.community.scores.forms' });
  const [scoreList, setScoreList] = useState<ScoreCardFormData>({
    name: '',
    description: '',
    memberEditAccess: false,
    containsAllMembers: true,
    ascending: false,
    type: ScoreCardType.COUNT,
  });
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);

  useEffect(() => {
    if (initialState) {
      const initialSelectedIds = initialState.selectedUserIds ?? [];
      setScoreList({
        ...initialState,
        selectedUserIds: initialSelectedIds,
        type: initialState.type ?? ScoreCardType.COUNT,
      });
      setSelectedMembers(initialSelectedIds);
    } else {
      setScoreList({
        name: '',
        description: '',
        memberEditAccess: false,
        containsAllMembers: true,
        ascending: false,
        type: ScoreCardType.COUNT,
      });
      setSelectedMembers([]);
    }
  }, [initialState]);

  useEffect(() => {
    if (!open) {
      setScoreList({
        name: '',
        description: '',
        memberEditAccess: false,
        containsAllMembers: true,
        ascending: false,
        type: ScoreCardType.COUNT,
      });
      setSelectedMembers([]);
    }
  }, [open]);

  useEffect(() => {
    // keep selected ids in form state
    setScoreList((prev) => ({ ...prev, selectedUserIds: selectedMembers }));
  }, [selectedMembers]);

  const validateAndSubmit = async () => {
    if (!scoreList.name || scoreList.name.trim() === '') {
      toast.error(t('errors.nameRequired'));
      return;
    }

    if (!communityId) {
      toast.error(t('errors.noCommunity'));
      return;
    }

    if (!scoreList.type) {
      toast.error(t('errors.typeRequired'));
      return;
    }

    try {
      const created = await createScoreCard({
        ...scoreList,
        communityId,
        containsAllMembers: scoreList.containsAllMembers ?? true,
        ascending: scoreList.ascending ?? false,
        type: scoreList.type,
      });
      toast.success(t('createSuccess') || t('createButton'));
      onCreated?.(created);
      setOpen(false);
    } catch (err: any) {
      console.error('Failed to create score card', err);
      if (err.message === 'UNAUTHORIZED') toast.error(t('errors.unauthorized'));
      else toast.error(err?.response?.data?.message || t('errors.createError'));
      throw err;
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} title={t('createTitle')}>
      <div className="flex flex-col gap-4 pt-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-primary">{t('name')}</label>
          <input
            value={scoreList.name}
            onChange={(e) => setScoreList((prev) => ({ ...prev, name: e.target.value }))}
            type="text"
            placeholder={t('namePlaceholder')}
            className="w-full rounded-lg border-none !bg-secondary-50 p-2"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-primary">{t('description')}</label>
          <textarea
            value={scoreList.description ?? ''}
            onChange={(e) => setScoreList((prev) => ({ ...prev, description: e.target.value }))}
            placeholder={t('descriptionPlaceholder')}
            rows={3}
            className="w-full rounded-lg border-none !bg-secondary-50 p-2"
          />
        </div>

        <div>
          <p className="mb-2 font-medium">{t('typeLabel')}</p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {Object.values(ScoreCardType).map((type) => {
              const isSelected = scoreList.type === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setScoreList((prev) => ({ ...prev, type }))}
                  className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-secondary-100 hover:bg-secondary-100 bg-secondary-50 text-details/80'
                  }`}>
                  {t(`type.${getScoreCardTypeLabelKey(type)}`)}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="font-medium">{t('membersCanEdit')}</p>
          <div className="mt-2">
            <ToggleSwitch
              leftLabel={t('no')}
              rightLabel={t('yes')}
              value={!!scoreList.memberEditAccess}
              onChange={(value) => setScoreList((prev) => ({ ...prev, memberEditAccess: value }))}
            />
          </div>
        </div>

        <div>
          <p className="font-medium">{t('includeAllMembers')}</p>
          <div className="mt-2">
            <ToggleSwitch
              leftLabel={t('no')}
              rightLabel={t('yes')}
              value={!!scoreList.containsAllMembers}
              onChange={(value) => setScoreList((prev) => ({ ...prev, containsAllMembers: value }))}
            />
          </div>
        </div>

        <div>
          <p className="font-medium">{t('ascending')}</p>
          <div className="mt-2">
            <ToggleSwitch
              leftLabel={t('highest')}
              rightLabel={t('lowest')}
              value={!!scoreList.ascending}
              onChange={(value) => setScoreList((prev) => ({ ...prev, ascending: value }))}
            />
          </div>
        </div>

        {!scoreList.containsAllMembers && (
          <div>
            <p className="mb-2 font-medium">{t('selectMembers')}</p>
            <ParticipantPicker communityId={communityId} selected={selectedMembers} setSelected={setSelectedMembers} />
          </div>
        )}

        <div className="mt-4 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={validateAndSubmit}>
            {t('createButton')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
